import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";
import { cn } from "@/lib/cn";

/**
 * Exported so a real <Link> can wear the button's clothes. A row action that
 * navigates must BE a link — otherwise cmd-click, middle-click and "open in new
 * tab" all silently do nothing, and screen readers announce a button that
 * moves the page.
 */
export const buttonStyles = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-sm font-medium " +
    "transition-[background,border,color,box-shadow] duration-150 select-none " +
    "disabled:pointer-events-none disabled:opacity-45 " +
    "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-bright",
  {
    variants: {
      variant: {
        primary:
          "bg-brand text-brand-on hover:bg-brand-ink active:bg-brand-ink shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]",
        secondary:
          "bg-surface text-ink border border-line-strong hover:bg-sunken hover:border-ink-faint active:bg-sunken",
        ghost: "bg-transparent text-ink-soft hover:bg-sunken hover:text-ink",
        brass:
          "bg-brass text-white hover:bg-[color:var(--color-brass-bright)] active:brightness-95",
        danger:
          "bg-surface text-danger border border-[color:var(--color-danger)]/30 hover:bg-danger-tint",
        link: "bg-transparent text-brand underline-offset-4 hover:underline px-0 h-auto",
      },
      size: {
        sm: "h-7 px-2.5 text-xs",
        md: "h-9 px-3.5 text-sm",
        lg: "h-10 px-5 text-base",
        icon: "h-9 w-9 p-0",
        "icon-sm": "h-7 w-7 p-0",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonStyles({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";
